// Wrappers for the prerequisite-test endpoints, mounted under `/bmd/*` in
// tutor-backend alongside the base BMD assignment routes.
//
// Use the shared `apiClient` from `src/infra/api-client.ts`, not raw axios: it
// handles Bearer-token injection and the expired-token redirect.

import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  BmdPrereqCandidatesResponse,
  BmdPrereqCreatePayload,
  BmdPrereqCreateResponse,
  BmdPrereqPreviewRequest,
  BmdPrereqPreviewResponse,
} from "../types/bmd-prereq.types"

const CANDIDATES_ENDPOINT = "bmd/prereqCandidates"
const PREVIEW_ENDPOINT = "bmd/prereqPreview"
const CREATE_ENDPOINT = "bmd/createPrereqAssignment"

/** The student's base diagnostics a prerequisite test could be built from. */
export async function getBmdPrereqCandidates(
  studentKey: string,
  signal?: AbortSignal,
): Promise<BmdPrereqCandidatesResponse> {
  const response = await apiClient.post<BmdPrereqCandidatesResponse>(
    BASE_URL + CANDIDATES_ENDPOINT,
    { student_key: studentKey },
    { signal },
  )
  const data = response.data
  return {
    candidates: Array.isArray(data?.candidates) ? data.candidates : [],
  }
}

/** What the prerequisite test would contain right now. Read-only. */
export async function getBmdPrereqPreview(
  req: BmdPrereqPreviewRequest,
  signal?: AbortSignal,
): Promise<BmdPrereqPreviewResponse> {
  const response = await apiClient.post<BmdPrereqPreviewResponse>(
    BASE_URL + PREVIEW_ENDPOINT,
    {
      diagnostics_session_key: req.diagnostics_session_key,
      student_key: req.student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    ...data,
    clusters: Array.isArray(data?.clusters) ? data.clusters : [],
    // null means the test is unrestricted, so it must not be flattened into
    // an empty array here.
    selected_strand_ids: Array.isArray(data?.selected_strand_ids)
      ? data.selected_strand_ids
      : null,
    skipped_no_dq: Array.isArray(data?.skipped_no_dq) ? data.skipped_no_dq : [],
  }
}

/** Creates the prerequisite test as its own activity. */
export async function createBmdPrereqAssignment(
  payload: BmdPrereqCreatePayload,
  signal?: AbortSignal,
): Promise<BmdPrereqCreateResponse> {
  const response = await apiClient.post<BmdPrereqCreateResponse>(
    BASE_URL + CREATE_ENDPOINT,
    {
      diagnostics_session_key: payload.diagnostics_session_key,
      session_key: payload.session_key,
      student_key: payload.student_key,
      lesson_key: payload.lesson_key || "",
      is_homework: payload.is_homework ?? 0,
      // Omitted entirely when there is no restriction, because the server
      // reads an absent field as "every cluster" and an empty array as an
      // error.
      ...(payload.strand_ids && payload.strand_ids.length
        ? { strand_ids: payload.strand_ids }
        : {}),
    },
    { signal },
  )
  return response.data
}
